import { validatePayload } from './validate';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Field, FieldGroup, FieldLabel, FieldLegend, FieldSet } from '@/components/ui/field';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { ClinicalTimeField, localDateTime, toOccurredAt } from './ClinicalEventEntry';
import { epdsScore } from './postpartumCourseProjection';
import type { ProposedEntry } from './PrenatalEntry';
import type { EventType } from './catalog.generated';
export function EpdsEntry({
  subjectId,
  timeZone,
  disabled,
  onSave,
  reviewedThreshold,
}: {
  subjectId: string;
  timeZone: string;
  disabled?: boolean;
  onSave: (entries: ProposedEntry[], occurredAt: string) => Promise<void>;
  reviewedThreshold?: number | null;
}) {
  const [items, setItems] = useState<string[]>(Array(10).fill('')),
    [instrument, setInstrument] = useState(''),
    [notes, setNotes] = useState(''),
    [safety, setSafety] = useState(''),
    [time, setTime] = useState(() => localDateTime(new Date(), timeZone)),
    [busy, setBusy] = useState(false),
    [error, setError] = useState('');
  const score = epdsScore(items.map((v) => (v === '' ? null : Number(v))));
  const positive = items[9] !== '' && Number(items[9]) > 0;
  async function save() {
    setError('');
    try {
      if (!subjectId || !score || !instrument.trim())
        throw Error('Enter all ten scored items and identify the instrument version / language.');
      if (positive && !safety.trim())
        throw Error('Document the safety assessment / action taken for the positive item 10.');
      const payload = {
        item_scores: items.map(Number),
        instrument_version: instrument.trim(),
        ...(notes.trim() || safety.trim()
          ? {
              notes: [notes.trim(), safety.trim() ? `Item 10 safety assessment / action: ${safety.trim()}` : '']
                .filter(Boolean)
                .join('\n'),
            }
          : {}),
      };
      const errors = validatePayload('PP.EPDS', payload, 1);
      if (errors.length) throw Error(errors.join('; '));
      setBusy(true);
      await onSave(
        [
          {
            event_type: 'PP.EPDS' as EventType,
            schema_version: 1,
            subject_id: subjectId,
            payload,
          },
        ],
        toOccurredAt(time, timeZone),
      );
      setItems(Array(10).fill(''));
      setNotes('');
      setSafety('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unable to save assessment.');
    } finally {
      setBusy(false);
    }
  }
  return (
    <FieldSet disabled={disabled || busy}>
      <FieldLegend>EPDS scored-item assessment</FieldLegend>
      <p className="text-sm text-muted-foreground">
        Enter each already-scored value (0–3) from the completed, validated questionnaire. These are scores, not answer
        positions; some questions are reverse-scored. No questionnaire wording is reproduced here.{' '}
        <a
          className="underline"
          href="https://www.cope.org.au/health-professionals/screening-and-assessment-tools/using-the-epds-as-a-screening-tool"
          target="_blank"
          rel="noreferrer">
          Scoring and interpretation guidance
        </a>
        .
      </p>
      <ClinicalTimeField value={time} onChange={setTime} timeZone={timeZone} />
      <FieldGroup>
        <Field>
          <FieldLabel htmlFor="epds-instrument">Instrument version / language</FieldLabel>
          <Input
            id="epds-instrument"
            className="min-h-11"
            value={instrument}
            onChange={(e) => setInstrument(e.target.value)}
          />
        </Field>
        <div className="grid gap-3 sm:grid-cols-2">
          {items.map((v, i) => (
            <Field key={i}>
              <FieldLabel>Item {i + 1} scored value</FieldLabel>
              <ToggleGroup
                type="single"
                variant="outline"
                value={v}
                onValueChange={(value) => setItems(items.map((old, n) => (n === i ? value : old)))}
                aria-label={`Item ${i + 1} scored value`}
                className="justify-start">
                {[0, 1, 2, 3].map((value) => (
                  <ToggleGroupItem key={value} value={String(value)} className="min-h-11 min-w-11">
                    {value}
                  </ToggleGroupItem>
                ))}
              </ToggleGroup>
            </Field>
          ))}
        </div>
        <p>EPDS total: {score ? `${score.total}/30` : 'Incomplete'}</p>
        {positive && (
          <Alert variant="destructive">
            <AlertTitle>Item 10 requires immediate safety assessment</AlertTitle>
            <AlertDescription>
              Review safety and document the assessment and action under the practice protocol. A total score does not
              cancel this finding.
            </AlertDescription>
          </Alert>
        )}
        {score && reviewedThreshold != null && score.total >= reviewedThreshold && (
          <Alert>
            <AlertDescription>
              The score meets the reviewed practice threshold ({reviewedThreshold}); arrange clinical review according
              to that protocol.
            </AlertDescription>
          </Alert>
        )}
        {positive && (
          <Field>
            <FieldLabel htmlFor="epds-safety">Safety assessment / action taken</FieldLabel>
            <Textarea id="epds-safety" value={safety} onChange={(e) => setSafety(e.target.value)} />
          </Field>
        )}
        <Field>
          <FieldLabel htmlFor="epds-notes">Assessment notes</FieldLabel>
          <Textarea id="epds-notes" value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Field>
      </FieldGroup>
      {error && (
        <p role="alert" className="text-destructive">
          {error}
        </p>
      )}
      <Button type="button" className="min-h-11" disabled={disabled || busy} onClick={save}>
        Save EPDS assessment
      </Button>
    </FieldSet>
  );
}
